import { type Api } from '../api'

export const toFulfillmentCity = (city: Api.City) => ({
  id: city.id,
  slug: city.slug,
  name: city.name,
  regionName: city.region_name,
  fulfillmentCount: city.fulfillment_count,
})

export type FulfillmentCity = ReturnType<typeof toFulfillmentCity>

const toServiceArea = (area: Api.ServiceArea) => ({
  mode: area.mode,
  cities: area.cities.map(toFulfillmentCity),
})

export const toFulfillmentLocation = (location: Api.Location) => ({
  id: location.id,
  address: location.address,
  city: toFulfillmentCity(location.city),
})

export type FulfillmentLocation = ReturnType<typeof toFulfillmentLocation>

const toRating = (rating: Api.Rating | null) =>
  rating
    ? {
        value: rating.rating,
        reviewCount: rating.review_count,
      }
    : null

export const toFulfillment = (fulfillment: Api.Fulfillment) => ({
  id: fulfillment.id,
  slug: fulfillment.slug,
  name: fulfillment.name,
  description: fulfillment.description,
  shipmentTypes: fulfillment.shipment_types,
  locations: fulfillment.locations.map(toFulfillmentLocation),
  serviceAreas: {
    fbs: fulfillment.service_areas.fbs ? toServiceArea(fulfillment.service_areas.fbs) : undefined,
    dbs: fulfillment.service_areas.dbs ? toServiceArea(fulfillment.service_areas.dbs) : undefined,
  },
  marketplaces: fulfillment.marketplaces.map((marketplace) => ({
    marketplace: marketplace.marketplace,
    otherName: marketplace.other_name,
    coverageMode: marketplace.coverage_mode,
    destinations: marketplace.destinations,
  })),
  categoryMode: fulfillment.category_mode,
  categoryExclusions: fulfillment.category_exclusions,
  deliveryFrequency: fulfillment.delivery_frequency,
  deliveryFrequencyLabel: fulfillment.delivery_frequency_label,
  otherShipmentDescription: fulfillment.other_shipment_description,
  areaSquareMeters: fulfillment.area_square_meters,
  operationStartedYear: fulfillment.operation_started_year,
  activeClientCount: fulfillment.active_client_count,
  minimumUnitPrice: fulfillment.minimum_unit_price,
  servicePrices: fulfillment.service_prices,
  serviceBlocks: fulfillment.service_blocks,
  yandexMapsUrl: fulfillment.yandex_maps_url,
  twoGisUrl: fulfillment.two_gis_url,
  websiteUrl: fulfillment.website_url,
  phone: fulfillment.phone,
  email: fulfillment.email,
  telegram: fulfillment.telegram,
  max: fulfillment.max,
  coverFileId: fulfillment.cover_file_id,
  ratings: {
    yandex: toRating(fulfillment.ratings.yandex),
    twoGis: toRating(fulfillment.ratings.two_gis),
    evirma: toRating(fulfillment.ratings.evirma),
    total: toRating(fulfillment.ratings.total),
  },
  canEdit: fulfillment.can_edit,
  createDate: fulfillment.create_date,
  modifyDate: fulfillment.modify_date,
})

export type Fulfillment = ReturnType<typeof toFulfillment>

export const toOwnerFulfillment = (fulfillment: Api.OwnerFulfillment) => ({
  ...toFulfillment(fulfillment),
  inn: fulfillment.inn,
})

export type OwnerFulfillment = ReturnType<typeof toOwnerFulfillment>
